import type { ReactNode } from 'react';
import { useHistory } from 'react-router-dom';
import { Modal, CustomFlowbiteTheme } from 'flowbite-react';
import { Button } from 'modules/v2/common/AtomicDesign/atoms';
import { CheckIconNew } from 'modules/v2/common/components/SvgIcon';
import { getRoutePlansAndBillings } from 'modules/v2/routes/navigation';
import { ConfirmationCheckIcon } from '../../../../../common/components';

interface ConfirmationModalProps {
  title: string;
  description?: ReactNode;
  /** Replaces the default check icon. */
  icon?: ReactNode;
  /** Shown above the title. */
  badge?: ReactNode;
  /** Small line under the action button. */
  footnote?: ReactNode;
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
  onConfirmation: () => void;
  confirmationText: string;
}

const defaultProps: Partial<ConfirmationModalProps> = {
  description: '',
  icon: null,
  badge: null,
  footnote: null,
};

const customTheme: CustomFlowbiteTheme['modal'] = {
  root: {
    show: {
      on: 'flex bg-gray-700 bg-opacity-50',
      off: 'hidden',
    },
  },
  content: {
    base: 'relative h-full w-fit w-[440px] md:h-auto max-[440px]:w-full',
  },
  footer: {
    base:
      'flex space-x-2 rounded-b border-neutral-200 min-h-[91px] px-6 py-[13px] dark:border-neutral-600',
  },
};

const ConfirmationModal = ({
  title,
  description,
  showModal,
  setShowModal,
  onConfirmation,
  confirmationText = 'Got it',
  icon,
  badge,
  footnote,
}: ConfirmationModalProps) => {
  const history = useHistory();

  return (
    <Modal
      className="font-figtree"
      theme={customTheme}
      show={showModal}
      onClose={() => setShowModal(false)}
    >
      <Modal.Body>
        <div className="mx-auto w-[52px] h-[52px] flex items-center justify-center">
          {icon || <ConfirmationCheckIcon />}
        </div>
        <div className="text-center w-[513px] max-[440px]:w-full sm:w-full mt-6">
          {badge && <div className="mb-3.5">{badge}</div>}
          <p className="text-base mb-3.5">{title}</p>
          {description && <p className="text-ms text-neutral-500">{description}</p>}
        </div>
        <div className="flex justify-center mt-6">
          <Button onClick={onConfirmation} type="dark" className="w-[116px]">
            {confirmationText}
          </Button>
        </div>
        {footnote && <p className="text-center text-xs text-neutral-500 mt-4">{footnote}</p>}
      </Modal.Body>
    </Modal>
  );
};

ConfirmationModal.defaultProps = defaultProps;

export default ConfirmationModal;
